import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { ConfirmProvider } from './components/ConfirmProvider';
import { useAppUpdate } from './hooks/useAppUpdate';
import Landing from './pages/Landing';
import AuthPage from './pages/AuthPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Contact from './pages/Contact';
import HelpCenter from './pages/HelpCenter';
import UserDashboard from './pages/UserDashboard';
import AdminDashboardMobile from './pages/AdminDashboardMobile';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ActivityLogs from './pages/ActivityLogs';
import AdminsManagement from './pages/AdminsManagement';
import Notifications from './pages/Notifications';
import ArticlesPage from './pages/ArticlesPage';
import InstallGuide from './pages/InstallGuide';
import InstallationGuide from './pages/InstallationGuide';
import PublicPortal from './pages/PublicPortal';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AuthCallback from './pages/AuthCallback';
import ImpersonatePage from './pages/ImpersonatePage';
import EmbedAuthPage from './pages/EmbedAuthPage';
import BoardReport from './pages/BoardReport';
import NexusEventsPage from './pages/NexusEventsPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Support from './pages/Support';
import './App.css';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();

  if (user?.role === 'super_admin') {
    return <SuperAdminDashboard />;
  } else if (user?.role === 'admin') {
    return <AdminDashboardMobile />;
  } else {
    return <UserDashboard />;
  }
};

function AppContent() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();

  // Admins may be mid-edit, so prompt instead of reloading under them.
  const adminRoutes = ['/dashboard', '/notifications', '/articles', '/events', '/analytics', '/admins', '/activity-logs', '/install', '/super-admin'];
  useAppUpdate(adminRoutes.some((r) => pathname.startsWith(r)) ? 'prompt' : 'silent');

  return (
    <div className="App">
      <Routes>
        <Route path="/portal/:customerId" element={<PublicPortal />} />
        <Route path="/install-guide" element={<InstallGuide />} />
        <Route path="/installation-guide" element={<InstallationGuide />} />
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <AuthPage />} />
        <Route path="/signup" element={isAuthenticated ? <Navigate to="/dashboard" /> : <AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route path="/embed-auth" element={<EmbedAuthPage />} />
        <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/board-report" element={<BoardReport />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Landing />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardRouter />
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <PrivateRoute>
              <Notifications />
            </PrivateRoute>
          }
        />
        <Route
          path="/articles"
          element={
            <PrivateRoute>
              <ArticlesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/install"
          element={
            <PrivateRoute>
              <InstallGuide />
            </PrivateRoute>
          }
        />
        <Route
          path="/activity-logs"
          element={
            <PrivateRoute allowedRoles={['super_admin']}>
              <ActivityLogs />
            </PrivateRoute>
          }
        />
        <Route
          path="/admins"
          element={
            <PrivateRoute allowedRoles={['super_admin']}>
              <AdminsManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/events"
          element={
            <PrivateRoute>
              <NexusEventsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <PrivateRoute allowedRoles={['admin', 'super_admin']}>
              <AnalyticsDashboard />
            </PrivateRoute>
          }
        />
      </Routes>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <AppContent />
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;